/** @jsxImportSource @emotion/react */
import {
  createStyles,
  Table,
  Typography,
  SearchInput,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { ReactNode, useCallback, useState } from 'react';
import { Header } from '../../../../components';
import { useSession } from '../../../../context/Session';
import { Role } from '../../../../types';

interface Props {
  roles: Role[];
  setSelectedRole: (role: Role | 'new') => void;
}

export const RoleSearch = ({ roles, setSelectedRole }: Props) => {
  const session = useSession();
  const styles = useStyles();

  const canEditRoles =
    session.authenticated && session.permissions.includes('edit_groups');

  const [searchValue, setSearchValue] = useState<string>('');

  const headers = [{ label: 'User role', cellKey: 'userRole' }];

  const rows = roles
    .map(({ name }) => ({ value: { userRole: name } }))
    .filter(({ value: { userRole } }) => {
      if (searchValue === '') {
        return true;
      }
      return userRole
        .toLocaleLowerCase()
        .includes(searchValue.toLocaleLowerCase());
    });

  const handleSelectRole = useCallback(
    (roleName: string | number | undefined | ReactNode) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const role = roles.find((r) => r.name === roleName)!;
      setSelectedRole(role);
    },
    [setSelectedRole, roles]
  );

  return (
    <div>
      <Header>
        <div css={[styles.default]}>
          <div css={styles.titleContainer}>
            <Typography variant="subtitle">User Roles</Typography>
          </div>
          <SearchInput
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          {canEditRoles && (
            <UtilityButton
              text="Add user role"
              variant="primary"
              icon="plus"
              onClick={() => setSelectedRole('new')}
            />
          )}
        </div>
      </Header>
      <Table
        headers={headers}
        rows={rows}
        onRowClick={(row) => handleSelectRole(row.userRole)}
      />
    </div>
  );
};

const useStyles = createStyles(({ spacing, palette }) => ({
  default: {
    display: 'grid',
    gap: spacing.xxs,
    alignItems: 'center',
    gridTemplateColumns: '2fr 1fr auto',
    'svg:not(button *)': {
      color: palette.grey.x900,
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
}));
